'use client';

import { useState, useEffect } from 'react';
import { X, User, Bot, ThumbsUp, ThumbsDown, AlertCircle, Copy, Check } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  model?: string;
  feedback?: {
    thumbsUp?: boolean;
    thumbsDown?: boolean;
    text?: string;
  };
  toolCalls?: any[];
  error?: any;
}

interface Conversation {
  id: string;
  userId: string;
  agentId: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  messages: Message[];
}

interface ConversationModalProps {
  conversationId: string;
  agentId: string;
  onClose: () => void;
}

export default function ConversationModal({ conversationId, agentId, onClose }: ConversationModalProps) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  useEffect(() => {
    fetchConversation();
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/chat/agents/${agentId}/admin/conversations/${conversationId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch conversation');
      }

      const data = await response.json();
      setConversation(data.conversation);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Conversation Details
              </h2>
              {conversation && (
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                  <span>ID: {conversation.id}</span>
                  <span>User: {conversation.userId}</span>
                  {conversation.title && <span>{conversation.title}</span>}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-500 dark:text-gray-400">Loading conversation...</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-500 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {conversation && !loading && !error && (
            <div className="space-y-4">
              {conversation.messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-700 text-white'
                    }`}>
                      {message.role === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Bot className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block max-w-[85%] ${message.role === 'user' ? 'text-left' : ''}`}>
                      {/* Message Header */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {message.role === 'user' ? 'User' : 'Assistant'}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatTimestamp(message.timestamp)}
                        </span>
                        {message.model && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            ({message.model})
                          </span>
                        )}
                      </div>

                      {/* Message Body */}
                      <div className={`rounded-lg px-4 py-2 ${
                        message.role === 'user'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                      }`}>
                        <div className="whitespace-pre-wrap break-words">{message.content}</div>

                        {/* Tool Calls */}
                        {message.toolCalls && message.toolCalls.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-xs opacity-75">
                              Tool calls: {message.toolCalls.length}
                            </div>
                          </div>
                        )}

                        {/* Error */}
                        {message.error && (
                          <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800">
                            <div className="text-xs text-red-200">
                              Error: {JSON.stringify(message.error)}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Message Actions */}
                      <div className="flex items-center gap-2 mt-1">
                        {/* Copy Button */}
                        <button
                          onClick={() => copyToClipboard(message.content, message.id)}
                          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </button>

                        {/* Feedback */}
                        {message.feedback && (
                          <div className="flex items-center gap-2">
                            {message.feedback.thumbsUp && (
                              <div className="flex items-center gap-1 text-green-500">
                                <ThumbsUp className="h-3 w-3" />
                                <span className="text-xs">Liked</span>
                              </div>
                            )}
                            {message.feedback.thumbsDown && (
                              <div className="flex items-center gap-1 text-red-500">
                                <ThumbsDown className="h-3 w-3" />
                                <span className="text-xs">Disliked</span>
                              </div>
                            )}
                            {message.feedback.text && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                "{message.feedback.text}"
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {conversation && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Created: {formatTimestamp(conversation.createdAt)} |
              Updated: {formatTimestamp(conversation.updatedAt)} |
              Messages: {conversation.messages.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}